/**
 * LIA AU TRAVAIL DANS UNE CONVERSATION (To-do du 29/10/2026) : des qu'un
 * message mentionne @Lia, une bulle de Lia tient la place de sa reponse, pour
 * TOUS les membres, jusqu'a ce que la reponse arrive. Ce fichier est la regle,
 * pure et testee (`tests/lia-au-travail.test.mjs`) ; le rendu est
 * `LiaAuTravail.tsx`, le meme que la conversation privee avec Lia.
 *
 * Le serveur ecrit `conversations/{id}/aiHistory/lia-<messageId>` (le suivi) et
 * la reponse porte l'id `lia-<messageId>` : une question dont cette reponse
 * n'est pas dans la liste affichee est ATTENDUE. Le suivi ne decide que de
 * l'etape montree ; l'age de la question decide du reste, si bien que
 * l'affichage est immediat, avant meme que le serveur ait ecrit son suivi, et
 * qu'une Function morte sans rien ecrire finit par le dire.
 */
import { AUTEUR_LIA, AUTEUR_SYSTEME, type MessageVue } from './port.js';

/** Le delai au bout duquel une question sans reponse est dite perdue (la Function meurt a 300 s). */
export const DELAI_REPONSE_LIA_MS = 300_000;
/** Au-dela, une question sans reponse n'est plus une attente : on ne l'affiche plus. */
export const OUBLI_DE_L_ATTENTE_LIA_MS = 24 * 3_600_000;
/** Ce que la bulle dit quand le delai est depasse (le texte du message systeme du serveur). */
export const TEXTE_REPONSE_LIA_PERDUE = "Lia n'a pas répondu à temps. Reposez la question.";

/** Au-dela de ce nombre de secondes, la bulle dit que Lia est toujours en cours. */
export const SECONDES_AVANT_LE_COMPTEUR = 15;
/** Au-dela, elle dit que c'est plus long que d'habitude. */
export const SECONDES_AVANT_LE_LONG = 60;

/** L'etape que Lia annonce (`etape` du suivi), comme la conversation privee la montre. */
export interface EtapeDeLiaVue {
  label: string;
  kind: string;
  state: 'running' | 'done' | 'error';
  /** `denied` : l'etape a ete refusee (dit « Refusé » au lieu de « Échec »). */
  outcome?: string | undefined;
}

/** Un suivi de Lia en cours, tel que le port le rend (`sdk.messages.observerSuivisLia`). */
export interface SuiviLiaVue {
  /** La QUESTION (le message qui mentionne Lia). */
  messageId: string;
  parentId: string | null;
  etape: EtapeDeLiaVue | null;
  at: Date;
}

/** Une attente a montrer : la bulle de Lia a la place de sa reponse. */
export interface AttenteLia {
  /** L'id de la question ; l'attente porte la clef `lia-<messageId>`, celle de la reponse. */
  messageId: string;
  /** Depuis quand Lia travaille : l'instant de la question. */
  depuis: Date;
  etape: EtapeDeLiaVue | null;
  /** Le delai est depasse sans reponse : la bulle le dit au lieu de charger. */
  echec: string | null;
}

/** L'id de la reponse de Lia a une question (`idDeReponse`, `functions/src/conversations/contrat.ts`). */
export function idDeReponseDeLia(messageId: string): string {
  return `lia-${messageId}`;
}

/**
 * LES ATTENTES DE LIA a montrer dans la liste AFFICHEE (`dansFil` : le fil d'un
 * message, sinon la conversation principale), du plus ancien au plus recent.
 * La reponse a une question de la conversation est une racine, celle d'une
 * question de fil est dans le meme fil : on ne regarde donc que les questions
 * racines dans la vue principale, et celles du fil dans un fil. `messages` est
 * la liste telle que la vue la lit ; `suivis` les suivis EN COURS.
 */
export function liaEnAttente(
  messages: readonly (Omit<Pick<MessageVue, 'id' | 'parentId' | 'auteur' | 'mentions'>, 'parentId'> & { parentId?: string | null | undefined; createdAt: Date | null | undefined })[],
  suivis: readonly SuiviLiaVue[],
  maintenant: number,
  dansFil = false,
): AttenteLia[] {
  const ids = new Set(messages.map((m) => m.id));
  const attentes: AttenteLia[] = [];
  for (const m of messages) {
    if (m.mentions?.lia !== true || m.auteur === undefined || m.auteur === AUTEUR_LIA || m.auteur === AUTEUR_SYSTEME) continue;
    if (dansFil ? !m.parentId : Boolean(m.parentId)) continue;
    if (ids.has(idDeReponseDeLia(m.id))) continue;
    // Un envoi de ce poste pas encore date par le serveur n'a pas de Date : sa question vient de partir.
    const depuis = m.createdAt instanceof Date ? m.createdAt : new Date(maintenant);
    const age = maintenant - depuis.getTime();
    if (!Number.isFinite(age) || age >= OUBLI_DE_L_ATTENTE_LIA_MS) continue;
    if (age >= DELAI_REPONSE_LIA_MS) {
      attentes.push({ messageId: m.id, depuis, etape: null, echec: TEXTE_REPONSE_LIA_PERDUE });
      continue;
    }
    attentes.push({ messageId: m.id, depuis, etape: suivis.find((s) => s.messageId === m.id)?.etape ?? null, echec: null });
  }
  return attentes.sort((a, b) => a.depuis.getTime() - b.depuis.getTime());
}
